import React, { useState, useEffect } from "react";
import { MDBCard, MDBCardBody, MDBBtn, MDBIcon } from "mdb-react-ui-kit";
import { usePageState } from "../context/AppStateContext";
import { useMessageBox } from "../components/Common/MessageBox";
import "./EbaySettingsPage.css";

const EbaySettingsPage = () => {
  /* ===================== STATE PERSISTENCE ===================== */
  const { state: persistedState, setState: setPersistedState } =
    usePageState("ebaySettings");
  const { showError, showSuccess, MessageBoxComponent } = useMessageBox();

  /* ===================== STATE ===================== */

  const [ebayData, setEbayData] = useState({
    fulfillmentPolicies: [],
    paymentPolicies: [],
    returnPolicies: [],
    locations: [],
    MarketplaceId: "",
    defaultCurrency: "POUND",
    program: [],
  });

  // Initialize selected from persisted state
  const [selected, setSelected] = useState(() => ({
    fulfillment: persistedState.selected?.fulfillment || "",
    payment: persistedState.selected?.payment || "",
    return: persistedState.selected?.return || "",
    location: persistedState.selected?.location || "",
    program: persistedState.selected?.program || "",
  }));

  // Sync selected state to context for persistence
  useEffect(() => {
    setPersistedState({ selected });
  }, [selected, setPersistedState]);

  /* ===================== EBAY OAUTH CALLBACK ===================== */

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (!code) return;

    const company_id = localStorage.getItem("company_id");
    if (!company_id) return;

    const exchangeEbayCode = async () => {
      try {
        const res = await fetch(
          "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/ebayGetAuthTokenotherusers",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code, company_id }),
          },
        );

        const data = await res.json();
        if (!res.ok) {
          console.error("eBay auth failed:", data);
          return;
        }

        console.log("eBay connected successfully");

        // URL clean
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      } catch (err) {
        console.error("OAuth exchange error:", err);
      }
    };

    exchangeEbayCode();
  }, []);

  /* ===================== FETCH EBAY POLICIES ===================== */

  useEffect(() => {
    const company_id = localStorage.getItem("company_id");
    if (!company_id) return;

    const fetchEbayPolicies = async () => {
      try {
        const company_id = localStorage.getItem("company_id");

        // ebay policies
        const res = await fetch(
          `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getEbayPoliciesByCompany/${company_id}`,
        );

        const result = await res.json();
        if (!res.ok || !result.success) return;

        const data = result.data;

        const currencyMap = {
          EBAY_US: "USD",
          EBAY_GB: "GBP",
          EBAY_DE: "EUR",
        };

        setEbayData({
          fulfillmentPolicies: data.fulfillmentPolicies || [],
          paymentPolicies: data.paymentPolicies || [],
          returnPolicies: data.returnPolicies || [],
          locations: data.locations || [],
          MarketplaceId: data.marketplaceId || "",
          defaultCurrency: currencyMap[data.marketplaceId] || "USD",
          program: data.businessPolicyProgram || [],
        });

        // get saved policies from DB
        const savedRes = await fetch(
          `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getSavedEbayPolicies/${company_id}`,
        );

        const saved = await savedRes.json();

        if (saved.success && saved.data) {
          // DB values override defaults
          setSelected({
            fulfillment: saved.data.shipping_policy_id,
            payment: saved.data.payment_policy_id,
            return: saved.data.return_policy_id,
            location: saved.data.location, // merchantLocationKey
            program: saved.data.business_policy,
          });
        } else {
          // fallback (first option)
          setSelected({
            fulfillment:
              data.fulfillmentPolicies?.[0]?.fulfillmentPolicyId || "",
            payment: data.paymentPolicies?.[0]?.paymentPolicyId || "",
            return: data.returnPolicies?.[0]?.returnPolicyId || "",
            location: data.locations?.[0]?.merchantLocationKey || "",
            program: data.businessPolicyProgram?.[0]?.programType || "",
          });
        }
      } catch (err) {
        console.error("Fetch error:", err);
      }
    };

    fetchEbayPolicies();
  }, []);

  /* ===================== HANDLERS ===================== */

  const handleChange = (key, value) => {
    setSelected((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const company_id = localStorage.getItem("company_id");
    if (!company_id) {
      showError("Company not found");
      return;
    }

    try {
      const res = await fetch(
        "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/saveEbayPolicies",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            company_id,
            shipping_policy_id: selected.fulfillment,
            payment_policy_id: selected.payment,
            return_policy_id: selected.return,
            location: selected.location,
            business_policy: selected.program,
          }),
        },
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        showError("Failed to save eBay policies");
        return;
      }

      showSuccess("eBay policies saved successfully");
    } catch (err) {
      console.error(err);
      showError("Server error");
    }
  };

  /* ===================== UI ===================== */

  return (
    <div className="ebay-settings-page">
      <h1 className="page-title">eBay Settings</h1>

      <MDBCard className="settings-card">
        <MDBCardBody>
          {/* Marketplace Info */}
          <div className="dropdown-section">
            <h4>Marketplace Info</h4>
            <p>Marketplace: {ebayData.MarketplaceId}</p>
            <p>Currency: {ebayData.defaultCurrency}</p>
          </div>

          {/* Fulfillment Policies */}
          <div className="dropdown-section">
            <h4>Fulfillment Policies</h4>
            <select
              className="form-select"
              value={selected.fulfillment}
              onChange={(e) => handleChange("fulfillment", e.target.value)}
            >
              {ebayData.fulfillmentPolicies.map((f) => (
                <option
                  key={f.fulfillmentPolicyId}
                  value={f.fulfillmentPolicyId}
                >
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          {/* Payment Policies */}
          <div className="dropdown-section">
            <h4>Payment Policies</h4>
            <select
              className="form-select"
              value={selected.payment}
              onChange={(e) => handleChange("payment", e.target.value)}
            >
              {ebayData.paymentPolicies.map((p) => (
                <option key={p.paymentPolicyId} value={p.paymentPolicyId}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Return Policies */}
          <div className="dropdown-section">
            <h4>Return Policies</h4>
            <select
              className="form-select"
              value={selected.return}
              onChange={(e) => handleChange("return", e.target.value)}
            >
              {ebayData.returnPolicies.map((r) => (
                <option key={r.returnPolicyId} value={r.returnPolicyId}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          {/* Locations */}
          <div className="dropdown-section">
            <h4>Locations</h4>
            <select
              className="form-select"
              value={selected.location}
              onChange={(e) => handleChange("location", e.target.value)}
            >
              {ebayData.locations.map((l) => (
                <option
                  key={l.merchantLocationKey}
                  value={l.merchantLocationKey}
                >
                  {l.name || l.merchantLocationKey}
                </option>
              ))}
            </select>
          </div>

          {/* Business Programs */}
          <div className="dropdown-section">
            <h4>Business Programs</h4>
            <select
              className="form-select"
              value={selected.program}
              onChange={(e) => handleChange("program", e.target.value)}
            >
              {ebayData.program.map((p, i) => (
                <option key={i} value={p.programType}>
                  {p.programType}
                </option>
              ))}
            </select>
          </div>

          <MDBBtn
            color="success"
            size="lg"
            className="mt-3"
            onClick={handleSave}
          >
            <MDBIcon icon="save" className="me-2" />
            Save Settings
          </MDBBtn>
        </MDBCardBody>
      </MDBCard>
      <MessageBoxComponent />
    </div>
  );
};

export default EbaySettingsPage;
